import React, { FC, memo, ReactElement, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import Button from '@material-ui/core/Button'
import CLanguageHandler from '../../../../NonReact/Languages/LanguageHandler'
import { LanguageContext, MessageContext, PromptContext } from '../../../../React/Common/Contexts'
import type { SelectOption } from '../../../../React/Common/Components/Selects/Select/Select'
import IconButton, { IconButtonType } from '../../../../React/Common/Components/IconButton'
import { IconName } from '../../../../React/Common/Components/Icon'
import type { MessageContextType } from '../../../../React/Common/Contexts/MessageContext'
import type { PromptDisplayType } from '../../../../React/Common/Contexts/PromptContext'
import { OutlinedSelect } from '../../Atoms/Inputs/OutlinedSelect'
import { OutlinedTextInput } from '../../Atoms/Inputs/OutlinedTextInput'
import { RouterCheckbox } from '../../Atoms/Inputs/RouterCheckbox'
import { PageTitle } from '../../Molecules/PageTitle/PageTitle'
import { DataTable, DataTableColumn } from '../../Molecules/DataTable/DataTable'
import { Router as RouterLang, LoggingSettingsLanguage } from '../../../../../i18n'
import { getLoggingSettings, putLoggingSettings } from '../../../../React/Common/API/ItemsGetters'
import {
    LoggingSettings,
    LoggingSettingsPutRequest,
    LoggingSettingsOption,
    LoggingSettingsResponseSettings,
    LoggingSettingsResponse
} from '../../../../NonReact/Types/LoggingSettingsTypes'
import { AdminBackendPermissionNames } from '../../../../React/Common/Constants/Permissions'
import { EMPTY_STRING } from '../../../../React/Common/Constants/Separators'
import { IsVarEmpty } from '../../../../NonReact/utils/GlobalFunctions'

const LOGGING_COMPONENT_ID_MAIN_SERVLET = 'mainServlet' as const
const LOGGING_COMPONENT_ID_DATABASE = 'database' as const
const LOGGING_COMPONENT_ID_REPOSITORY = 'repository' as const
const LOGGING_COMPONENT_ID_RPT_EXEC_MGR = 'rptExecMgr' as const
const LOGGING_COMPONENT_ID_MAIL_CONTROLLER = 'mailController' as const
const LOGGING_COMPONENT_ID_TASK_DELEGATOR = 'taskDelegator' as const
const LOGGING_COMPONENT_ID_TELEPHONE_MODULE = 'telephoneModule' as const
const LOGGING_COMPONENT_ID_OMNI_MODULE = 'omniModule' as const

type ComponentID =
    | typeof LOGGING_COMPONENT_ID_MAIN_SERVLET
    | typeof LOGGING_COMPONENT_ID_DATABASE
    | typeof LOGGING_COMPONENT_ID_REPOSITORY
    | typeof LOGGING_COMPONENT_ID_RPT_EXEC_MGR
    | typeof LOGGING_COMPONENT_ID_MAIL_CONTROLLER
    | typeof LOGGING_COMPONENT_ID_TASK_DELEGATOR
    | typeof LOGGING_COMPONENT_ID_TELEPHONE_MODULE
    | typeof LOGGING_COMPONENT_ID_OMNI_MODULE

type ComponentDefinition = {
    readonly id: ComponentID
    readonly label: string
    readonly enabledField: keyof LoggingSettings
    readonly backupField?: keyof LoggingSettings
    readonly levelField: keyof LoggingSettings
    readonly rollOverFilesField: keyof LoggingSettings
    readonly maxFileSizeField: keyof LoggingSettings
}

type LoggingSettingsRow = {
    readonly key: ComponentID
    readonly Description: string
    readonly component: ComponentDefinition
}

const LOGGING_SETTINGS_FIELDS: readonly (keyof LoggingSettings)[] = [
    'EnabledMainServlet',
    'BackupLogsMainServlet',
    'LevelMainServlet',
    'RollOverFilesMainServlet',
    'MaxFileSizeAsMBMainServlet',
    'EnabledDatabase',
    'BackupLogsDatabase',
    'LevelDatabase',
    'RollOverFilesDatabase',
    'MaxFileSizeAsMBDatabase',
    'EnabledRepository',
    'BackupLogsRepository',
    'LevelRepository',
    'RollOverFilesRepository',
    'MaxFileSizeAsMBRepository',
    'EnabledRptExecMgr',
    'BackupLogsRptExecMgr',
    'LevelRptExecMgr',
    'RollOverFilesRptExecMgr',
    'MaxFileSizeAsMBRptExecMgr',
    'EnabledMailController',
    'BackupLogsMailController',
    'LevelMailController',
    'RollOverFilesMailController',
    'MaxFileSizeAsMBMailController',
    'EnabledTaskDelegator',
    'BackupLogsTaskDelegator',
    'LevelTaskDelegator',
    'RollOverFilesTaskDelegator',
    'MaxFileSizeAsMBTaskDelegator',
    'EnabledTelephoneModule',
    'BackupLogsTelephoneModule',
    'LevelTelephoneModule',
    'RollOverFilesTelephoneModule',
    'MaxFileSizeAsMBTelephoneModule',
    'EnabledOmniModule',
    'LevelOmniModule',
    'RollOverFilesOmniModule',
    'MaxFileSizeAsMBOmniModule'
] as const

const DEFAULT_LOGGING_SETTINGS: LoggingSettings = {
    EnabledMainServlet: true,
    BackupLogsMainServlet: true,
    LevelMainServlet: 3,
    RollOverFilesMainServlet: 0,
    MaxFileSizeAsMBMainServlet: 25,
    EnabledDatabase: true,
    BackupLogsDatabase: true,
    LevelDatabase: 3,
    RollOverFilesDatabase: 0,
    MaxFileSizeAsMBDatabase: 50,
    EnabledRepository: true,
    BackupLogsRepository: true,
    LevelRepository: 3,
    RollOverFilesRepository: 0,
    MaxFileSizeAsMBRepository: 25,
    EnabledRptExecMgr: true,
    BackupLogsRptExecMgr: true,
    LevelRptExecMgr: 3,
    RollOverFilesRptExecMgr: 0,
    MaxFileSizeAsMBRptExecMgr: 50,
    EnabledMailController: true,
    BackupLogsMailController: true,
    LevelMailController: 3,
    RollOverFilesMailController: 0,
    MaxFileSizeAsMBMailController: 25,
    EnabledTaskDelegator: true,
    BackupLogsTaskDelegator: true,
    LevelTaskDelegator: 3,
    RollOverFilesTaskDelegator: 0,
    MaxFileSizeAsMBTaskDelegator: 25,
    EnabledTelephoneModule: true,
    BackupLogsTelephoneModule: true,
    LevelTelephoneModule: 3,
    RollOverFilesTelephoneModule: 2,
    MaxFileSizeAsMBTelephoneModule: 50,
    EnabledOmniModule: true,
    LevelOmniModule: 3,
    RollOverFilesOmniModule: 2,
    MaxFileSizeAsMBOmniModule: 50
}

const MAX_FILE_SIZE_AS_MB_MIN: number = 0
const MAX_FILE_SIZE_AS_MB_MAX: number = 10000

const clampInteger = (value: number, min: number, max: number): number => Math.max(min, Math.min(max, value))

const sanitizeLoggingSettings = (settings: LoggingSettings): LoggingSettings =>
    LOGGING_SETTINGS_FIELDS.reduce(
        (sanitized: LoggingSettings, field: keyof LoggingSettings): LoggingSettings => ({
            ...sanitized,
            [field]: settings[field]
        }),
        {} as LoggingSettings
    )

const mapResponseSettingsToLoggingSettings = (settings: LoggingSettingsResponseSettings): LoggingSettings => ({
    EnabledMainServlet:
        settings.EnabledMainServlet ?? settings.enabledMainServlet ?? DEFAULT_LOGGING_SETTINGS.EnabledMainServlet,
    BackupLogsMainServlet:
        settings.BackupLogsMainServlet ??
        settings.backupLogsMainServlet ??
        DEFAULT_LOGGING_SETTINGS.BackupLogsMainServlet,
    LevelMainServlet: settings.LevelMainServlet ?? settings.levelMainServlet ?? DEFAULT_LOGGING_SETTINGS.LevelMainServlet,
    RollOverFilesMainServlet:
        settings.RollOverFilesMainServlet ??
        settings.rollOverFilesMainServlet ??
        DEFAULT_LOGGING_SETTINGS.RollOverFilesMainServlet,
    MaxFileSizeAsMBMainServlet:
        settings.MaxFileSizeAsMBMainServlet ??
        settings.maxFileSizeAsMBMainServlet ??
        DEFAULT_LOGGING_SETTINGS.MaxFileSizeAsMBMainServlet,
    EnabledDatabase: settings.EnabledDatabase ?? settings.enabledDatabase ?? DEFAULT_LOGGING_SETTINGS.EnabledDatabase,
    BackupLogsDatabase:
        settings.BackupLogsDatabase ?? settings.backupLogsDatabase ?? DEFAULT_LOGGING_SETTINGS.BackupLogsDatabase,
    LevelDatabase: settings.LevelDatabase ?? settings.levelDatabase ?? DEFAULT_LOGGING_SETTINGS.LevelDatabase,
    RollOverFilesDatabase:
        settings.RollOverFilesDatabase ??
        settings.rollOverFilesDatabase ??
        DEFAULT_LOGGING_SETTINGS.RollOverFilesDatabase,
    MaxFileSizeAsMBDatabase:
        settings.MaxFileSizeAsMBDatabase ??
        settings.maxFileSizeAsMBDatabase ??
        DEFAULT_LOGGING_SETTINGS.MaxFileSizeAsMBDatabase,
    EnabledRepository:
        settings.EnabledRepository ?? settings.enabledRepository ?? DEFAULT_LOGGING_SETTINGS.EnabledRepository,
    BackupLogsRepository:
        settings.BackupLogsRepository ??
        settings.backupLogsRepository ??
        DEFAULT_LOGGING_SETTINGS.BackupLogsRepository,
    LevelRepository: settings.LevelRepository ?? settings.levelRepository ?? DEFAULT_LOGGING_SETTINGS.LevelRepository,
    RollOverFilesRepository:
        settings.RollOverFilesRepository ??
        settings.rollOverFilesRepository ??
        DEFAULT_LOGGING_SETTINGS.RollOverFilesRepository,
    MaxFileSizeAsMBRepository:
        settings.MaxFileSizeAsMBRepository ??
        settings.maxFileSizeAsMBRepository ??
        DEFAULT_LOGGING_SETTINGS.MaxFileSizeAsMBRepository,
    EnabledRptExecMgr: settings.EnabledRptExecMgr ?? settings.enabledRptExecMgr ?? DEFAULT_LOGGING_SETTINGS.EnabledRptExecMgr,
    BackupLogsRptExecMgr:
        settings.BackupLogsRptExecMgr ??
        settings.backupLogsRptExecMgr ??
        DEFAULT_LOGGING_SETTINGS.BackupLogsRptExecMgr,
    LevelRptExecMgr: settings.LevelRptExecMgr ?? settings.levelRptExecMgr ?? DEFAULT_LOGGING_SETTINGS.LevelRptExecMgr,
    RollOverFilesRptExecMgr:
        settings.RollOverFilesRptExecMgr ??
        settings.rollOverFilesRptExecMgr ??
        DEFAULT_LOGGING_SETTINGS.RollOverFilesRptExecMgr,
    MaxFileSizeAsMBRptExecMgr:
        settings.MaxFileSizeAsMBRptExecMgr ??
        settings.maxFileSizeAsMBRptExecMgr ??
        DEFAULT_LOGGING_SETTINGS.MaxFileSizeAsMBRptExecMgr,
    EnabledMailController:
        settings.EnabledMailController ?? settings.enabledMailController ?? DEFAULT_LOGGING_SETTINGS.EnabledMailController,
    BackupLogsMailController:
        settings.BackupLogsMailController ??
        settings.backupLogsMailController ??
        DEFAULT_LOGGING_SETTINGS.BackupLogsMailController,
    LevelMailController:
        settings.LevelMailController ?? settings.levelMailController ?? DEFAULT_LOGGING_SETTINGS.LevelMailController,
    RollOverFilesMailController:
        settings.RollOverFilesMailController ??
        settings.rollOverFilesMailController ??
        DEFAULT_LOGGING_SETTINGS.RollOverFilesMailController,
    MaxFileSizeAsMBMailController:
        settings.MaxFileSizeAsMBMailController ??
        settings.maxFileSizeAsMBMailController ??
        DEFAULT_LOGGING_SETTINGS.MaxFileSizeAsMBMailController,
    EnabledTaskDelegator:
        settings.EnabledTaskDelegator ?? settings.enabledTaskDelegator ?? DEFAULT_LOGGING_SETTINGS.EnabledTaskDelegator,
    BackupLogsTaskDelegator:
        settings.BackupLogsTaskDelegator ??
        settings.backupLogsTaskDelegator ??
        DEFAULT_LOGGING_SETTINGS.BackupLogsTaskDelegator,
    LevelTaskDelegator:
        settings.LevelTaskDelegator ?? settings.levelTaskDelegator ?? DEFAULT_LOGGING_SETTINGS.LevelTaskDelegator,
    RollOverFilesTaskDelegator:
        settings.RollOverFilesTaskDelegator ??
        settings.rollOverFilesTaskDelegator ??
        DEFAULT_LOGGING_SETTINGS.RollOverFilesTaskDelegator,
    MaxFileSizeAsMBTaskDelegator:
        settings.MaxFileSizeAsMBTaskDelegator ??
        settings.maxFileSizeAsMBTaskDelegator ??
        DEFAULT_LOGGING_SETTINGS.MaxFileSizeAsMBTaskDelegator,
    EnabledTelephoneModule:
        settings.EnabledTelephoneModule ??
        settings.enabledTelephoneModule ??
        DEFAULT_LOGGING_SETTINGS.EnabledTelephoneModule,
    BackupLogsTelephoneModule:
        settings.BackupLogsTelephoneModule ??
        settings.backupLogsTelephoneModule ??
        DEFAULT_LOGGING_SETTINGS.BackupLogsTelephoneModule,
    LevelTelephoneModule:
        settings.LevelTelephoneModule ??
        settings.levelTelephoneModule ??
        DEFAULT_LOGGING_SETTINGS.LevelTelephoneModule,
    RollOverFilesTelephoneModule:
        settings.RollOverFilesTelephoneModule ??
        settings.rollOverFilesTelephoneModule ??
        DEFAULT_LOGGING_SETTINGS.RollOverFilesTelephoneModule,
    MaxFileSizeAsMBTelephoneModule:
        settings.MaxFileSizeAsMBTelephoneModule ??
        settings.maxFileSizeAsMBTelephoneModule ??
        DEFAULT_LOGGING_SETTINGS.MaxFileSizeAsMBTelephoneModule,
    EnabledOmniModule: settings.EnabledOmniModule ?? settings.enabledOmniModule ?? DEFAULT_LOGGING_SETTINGS.EnabledOmniModule,
    LevelOmniModule: settings.LevelOmniModule ?? settings.levelOmniModule ?? DEFAULT_LOGGING_SETTINGS.LevelOmniModule,
    RollOverFilesOmniModule:
        settings.RollOverFilesOmniModule ??
        settings.rollOverFilesOmniModule ??
        DEFAULT_LOGGING_SETTINGS.RollOverFilesOmniModule,
    MaxFileSizeAsMBOmniModule:
        settings.MaxFileSizeAsMBOmniModule ??
        settings.maxFileSizeAsMBOmniModule ??
        DEFAULT_LOGGING_SETTINGS.MaxFileSizeAsMBOmniModule
})

const mapLoggingSettingsToPutRequest = (settings: LoggingSettings): LoggingSettingsPutRequest => ({
    enabledMainServlet: settings.EnabledMainServlet,
    backupLogsMainServlet: settings.BackupLogsMainServlet,
    levelMainServlet: settings.LevelMainServlet,
    rollOverFilesMainServlet: settings.RollOverFilesMainServlet,
    maxFileSizeAsMBMainServlet: settings.MaxFileSizeAsMBMainServlet,
    enabledDatabase: settings.EnabledDatabase,
    backupLogsDatabase: settings.BackupLogsDatabase,
    levelDatabase: settings.LevelDatabase,
    rollOverFilesDatabase: settings.RollOverFilesDatabase,
    maxFileSizeAsMBDatabase: settings.MaxFileSizeAsMBDatabase,
    enabledRepository: settings.EnabledRepository,
    backupLogsRepository: settings.BackupLogsRepository,
    levelRepository: settings.LevelRepository,
    rollOverFilesRepository: settings.RollOverFilesRepository,
    maxFileSizeAsMBRepository: settings.MaxFileSizeAsMBRepository,
    enabledRptExecMgr: settings.EnabledRptExecMgr,
    backupLogsRptExecMgr: settings.BackupLogsRptExecMgr,
    levelRptExecMgr: settings.LevelRptExecMgr,
    rollOverFilesRptExecMgr: settings.RollOverFilesRptExecMgr,
    maxFileSizeAsMBRptExecMgr: settings.MaxFileSizeAsMBRptExecMgr,
    enabledMailController: settings.EnabledMailController,
    backupLogsMailController: settings.BackupLogsMailController,
    levelMailController: settings.LevelMailController,
    rollOverFilesMailController: settings.RollOverFilesMailController,
    maxFileSizeAsMBMailController: settings.MaxFileSizeAsMBMailController,
    enabledTaskDelegator: settings.EnabledTaskDelegator,
    backupLogsTaskDelegator: settings.BackupLogsTaskDelegator,
    levelTaskDelegator: settings.LevelTaskDelegator,
    rollOverFilesTaskDelegator: settings.RollOverFilesTaskDelegator,
    maxFileSizeAsMBTaskDelegator: settings.MaxFileSizeAsMBTaskDelegator,
    enabledTelephoneModule: settings.EnabledTelephoneModule,
    backupLogsTelephoneModule: settings.BackupLogsTelephoneModule,
    levelTelephoneModule: settings.LevelTelephoneModule,
    rollOverFilesTelephoneModule: settings.RollOverFilesTelephoneModule,
    maxFileSizeAsMBTelephoneModule: settings.MaxFileSizeAsMBTelephoneModule,
    enabledOmniModule: settings.EnabledOmniModule,
    levelOmniModule: settings.LevelOmniModule,
    rollOverFilesOmniModule: settings.RollOverFilesOmniModule,
    maxFileSizeAsMBOmniModule: settings.MaxFileSizeAsMBOmniModule
})

const cloneSettings = (settings: LoggingSettings): LoggingSettings => sanitizeLoggingSettings(settings)

type LoggingSettingsCheckboxCellProps = {
    readonly testId: string
    readonly field: keyof LoggingSettings
    readonly checked: boolean
    readonly disabled: boolean
    readonly updateDraftSetting: (field: keyof LoggingSettings, value: number | boolean) => void
}

const LoggingSettingsCheckboxCell: FC<LoggingSettingsCheckboxCellProps> = memo(
    ({ testId, field, checked, disabled, updateDraftSetting }: LoggingSettingsCheckboxCellProps): ReactElement => {
        const handleChange = useCallback(
            (isChecked: boolean): void => {
                updateDraftSetting(field, isChecked)
            },
            [field, updateDraftSetting]
        )

        return (
            <div data-testid={testId}>
                <RouterCheckbox
                    className='logging-settings-list-cell-checkbox'
                    isChecked={checked}
                    disabled={disabled}
                    onChange={handleChange}
                />
            </div>
        )
    }
)

type LoggingSettingsSelectCellProps = {
    readonly testId: string
    readonly field: keyof LoggingSettings
    readonly value: number
    readonly disabled: boolean
    readonly options: readonly SelectOption<number>[]
    readonly containerClassName: string
    readonly updateDraftSetting: (field: keyof LoggingSettings, value: number | boolean) => void
}

const LoggingSettingsSelectCell: FC<LoggingSettingsSelectCellProps> = memo(
    ({
        testId,
        field,
        value,
        disabled,
        options,
        containerClassName,
        updateDraftSetting
    }: LoggingSettingsSelectCellProps): ReactElement => {
        const selectOptions: SelectOption<number>[] = useMemo((): SelectOption<number>[] => [...options], [options])

        const handleChange = useCallback(
            (newValue: number): void => {
                updateDraftSetting(field, newValue)
            },
            [field, updateDraftSetting]
        )

        return (
            <div data-testid={testId}>
                <OutlinedSelect<number>
                    containerClassName={containerClassName}
                    value={value}
                    disabled={disabled}
                    options={selectOptions}
                    handleChange={handleChange}
                />
            </div>
        )
    }
)

type LoggingSettingsNumberCellProps = {
    readonly testId: string
    readonly field: keyof LoggingSettings
    readonly value: number
    readonly disabled: boolean
    readonly updateDraftSetting: (field: keyof LoggingSettings, value: number | boolean) => void
}

const LoggingSettingsNumberCell: FC<LoggingSettingsNumberCellProps> = memo(
    ({ testId, field, value, disabled, updateDraftSetting }: LoggingSettingsNumberCellProps): ReactElement => {
        const handleChange = useCallback(
            (newValue: string): void => {
                const parsed: number = Number(newValue)
                const safeValue: number = Number.isNaN(parsed)
                    ? MAX_FILE_SIZE_AS_MB_MIN
                    : clampInteger(parsed, MAX_FILE_SIZE_AS_MB_MIN, MAX_FILE_SIZE_AS_MB_MAX)

                updateDraftSetting(field, safeValue)
            },
            [field, updateDraftSetting]
        )

        return (
            <div data-testid={testId}>
                <OutlinedTextInput
                    containerClassName='logging-settings-list-cell-input logging-settings-list-cell-number'
                    type='number'
                    min={MAX_FILE_SIZE_AS_MB_MIN}
                    max={MAX_FILE_SIZE_AS_MB_MAX}
                    value={value}
                    disabled={disabled}
                    onChange={handleChange}
                />
            </div>
        )
    }
)

/**
 * Routed logging settings table with inline editing controls.
 *
 * @returns Logging settings page content.
 */
export const LoggingSettingsList: FC = (): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle(RouterLang.NAME)
    language.loadBundle(LoggingSettingsLanguage.NAME)

    const { displayError, displayErrorMessage, displaySuccessMessage }: MessageContextType =
        useContext(MessageContext)
    const { displayPromptPromise }: PromptDisplayType = useContext(PromptContext)

    const [committedSettings, setCommittedSettings] = useState<Nullable<LoggingSettings>>()
    const [draftSettings, setDraftSettings] = useState<Nullable<LoggingSettings>>()
    const [levelOptions, setLevelOptions] = useState<readonly LoggingSettingsOption[]>([])
    const [rollOverOptions, setRollOverOptions] = useState<readonly LoggingSettingsOption[]>([])
    const [canEdit, setCanEdit] = useState<boolean>(false)
    const [readOnlyWarning, setReadOnlyWarning] = useState<string>(EMPTY_STRING)
    const [loadingCount, setLoadingCount] = useState<number>(0)

    const componentDefinitions: readonly ComponentDefinition[] = useMemo(
        (): readonly ComponentDefinition[] => [
            {
                id: LOGGING_COMPONENT_ID_MAIN_SERVLET,
                label: language.get(LoggingSettingsLanguage.LoggingSettings_Component_MainServlet),
                enabledField: 'EnabledMainServlet',
                backupField: 'BackupLogsMainServlet',
                levelField: 'LevelMainServlet',
                rollOverFilesField: 'RollOverFilesMainServlet',
                maxFileSizeField: 'MaxFileSizeAsMBMainServlet'
            },
            {
                id: LOGGING_COMPONENT_ID_DATABASE,
                label: language.get(LoggingSettingsLanguage.LoggingSettings_Component_Database),
                enabledField: 'EnabledDatabase',
                backupField: 'BackupLogsDatabase',
                levelField: 'LevelDatabase',
                rollOverFilesField: 'RollOverFilesDatabase',
                maxFileSizeField: 'MaxFileSizeAsMBDatabase'
            },
            {
                id: LOGGING_COMPONENT_ID_REPOSITORY,
                label: language.get(LoggingSettingsLanguage.LoggingSettings_Component_Repository),
                enabledField: 'EnabledRepository',
                backupField: 'BackupLogsRepository',
                levelField: 'LevelRepository',
                rollOverFilesField: 'RollOverFilesRepository',
                maxFileSizeField: 'MaxFileSizeAsMBRepository'
            },
            {
                id: LOGGING_COMPONENT_ID_RPT_EXEC_MGR,
                label: language.get(LoggingSettingsLanguage.LoggingSettings_Component_RptExecMgr),
                enabledField: 'EnabledRptExecMgr',
                backupField: 'BackupLogsRptExecMgr',
                levelField: 'LevelRptExecMgr',
                rollOverFilesField: 'RollOverFilesRptExecMgr',
                maxFileSizeField: 'MaxFileSizeAsMBRptExecMgr'
            },
            {
                id: LOGGING_COMPONENT_ID_MAIL_CONTROLLER,
                label: language.get(LoggingSettingsLanguage.LoggingSettings_Component_MailController),
                enabledField: 'EnabledMailController',
                backupField: 'BackupLogsMailController',
                levelField: 'LevelMailController',
                rollOverFilesField: 'RollOverFilesMailController',
                maxFileSizeField: 'MaxFileSizeAsMBMailController'
            },
            {
                id: LOGGING_COMPONENT_ID_TASK_DELEGATOR,
                label: language.get(LoggingSettingsLanguage.LoggingSettings_Component_TaskDelegator),
                enabledField: 'EnabledTaskDelegator',
                backupField: 'BackupLogsTaskDelegator',
                levelField: 'LevelTaskDelegator',
                rollOverFilesField: 'RollOverFilesTaskDelegator',
                maxFileSizeField: 'MaxFileSizeAsMBTaskDelegator'
            },
            {
                id: LOGGING_COMPONENT_ID_TELEPHONE_MODULE,
                label: language.get(LoggingSettingsLanguage.LoggingSettings_Component_TelephoneModule),
                enabledField: 'EnabledTelephoneModule',
                backupField: 'BackupLogsTelephoneModule',
                levelField: 'LevelTelephoneModule',
                rollOverFilesField: 'RollOverFilesTelephoneModule',
                maxFileSizeField: 'MaxFileSizeAsMBTelephoneModule'
            },
            {
                id: LOGGING_COMPONENT_ID_OMNI_MODULE,
                label: language.get(LoggingSettingsLanguage.LoggingSettings_Component_OmniModule),
                enabledField: 'EnabledOmniModule',
                levelField: 'LevelOmniModule',
                rollOverFilesField: 'RollOverFilesOmniModule',
                maxFileSizeField: 'MaxFileSizeAsMBOmniModule'
            }
        ],
        [language.getLanguageID()]
    )

    const rows: readonly LoggingSettingsRow[] = useMemo(
        (): readonly LoggingSettingsRow[] =>
            componentDefinitions.map(
                (component: ComponentDefinition): LoggingSettingsRow => ({
                    key: component.id,
                    Description: component.label,
                    component
                })
            ),
        [componentDefinitions]
    )

    const isDirty: boolean = useMemo((): boolean => {
        if (IsVarEmpty(committedSettings) || IsVarEmpty(draftSettings)) return false

        return JSON.stringify(committedSettings) !== JSON.stringify(draftSettings)
    }, [committedSettings, draftSettings])

    const isLoading: boolean = loadingCount > 0 || IsVarEmpty(committedSettings) || IsVarEmpty(draftSettings)

    const applyResponse = useCallback((response: Nullable<LoggingSettingsResponse>): void => {
        const settings: Definable<LoggingSettingsResponseSettings> = response?.Data?.Settings
        if (IsVarEmpty(settings)) return

        const sanitizedSettings: LoggingSettings = sanitizeLoggingSettings(mapResponseSettingsToLoggingSettings(settings))

        setCommittedSettings(sanitizedSettings)
        setDraftSettings(cloneSettings(sanitizedSettings))
        setLevelOptions(response?.Data?.LevelOptions ?? [])
        setRollOverOptions(response?.Data?.RollOverFilesOptions ?? [])
        setCanEdit(Boolean(response?.Data?.CanEdit))
        setReadOnlyWarning(response?.Data?.ReadOnlyWarning ?? EMPTY_STRING)
    }, [])

    const loadLoggingSettings = useCallback((): Promise<void> => {
        setLoadingCount((current: number): number => current + 1)

        return getLoggingSettings()
            .then((response: Nullable<LoggingSettingsResponse>): void => {
                applyResponse(response)
            })
            .catch((error: Error): void => {
                displayError(error)
            })
            .finally((): void => {
                setLoadingCount((current: number): number => current - 1)
            })
    }, [applyResponse, displayError])

    useEffect((): void => {
        loadLoggingSettings()
    }, [loadLoggingSettings])

    const updateDraftSetting = useCallback(
        (field: keyof LoggingSettings, value: number | boolean): void => {
            setDraftSettings((current: Nullable<LoggingSettings>): Nullable<LoggingSettings> => {
                if (IsVarEmpty(current)) return current

                return {
                    ...current,
                    [field]: value
                }
            })
        },
        []
    )

    const handleRefresh = useCallback((): void => {
        if (!isDirty) {
            loadLoggingSettings()
            return
        }

        displayPromptPromise(
            language.get(LoggingSettingsLanguage.LoggingSettings_RefreshConfirmBody),
            language.get(LoggingSettingsLanguage.LoggingSettings_RefreshConfirmTitle)
        )
            .then((): void => {
                loadLoggingSettings()
            })
            .catch((): void => {
                // cancelled
            })
    }, [displayPromptPromise, isDirty, language, loadLoggingSettings])

    const handleCancel = useCallback((): void => {
        if (IsVarEmpty(committedSettings)) return

        setDraftSettings(cloneSettings(committedSettings))
    }, [committedSettings])

    const handleSave = useCallback((): void => {
        if (!canEdit || !isDirty || IsVarEmpty(draftSettings)) return

        setLoadingCount((current: number): number => current + 1)

        putLoggingSettings(mapLoggingSettingsToPutRequest(sanitizeLoggingSettings(draftSettings)))
            .then((): Promise<void> => loadLoggingSettings())
            .then((): void => {
                displaySuccessMessage(language.get(LoggingSettingsLanguage.LoggingSettings_SaveSuccess))
            })
            .catch((): void => {
                displayErrorMessage(language.get(LoggingSettingsLanguage.LoggingSettings_SaveError))
            })
            .finally((): void => {
                setLoadingCount((current: number): number => current - 1)
            })
    }, [canEdit, displayErrorMessage, displaySuccessMessage, draftSettings, isDirty, language, loadLoggingSettings])

    const levelSelectOptions: readonly SelectOption<number>[] = useMemo(
        (): readonly SelectOption<number>[] =>
            levelOptions.map(
                (option: LoggingSettingsOption): SelectOption<number> => ({
                    label: option.Description,
                    value: option.Value
                })
            ),
        [levelOptions]
    )

    const rollOverSelectOptions: readonly SelectOption<number>[] = useMemo(
        (): readonly SelectOption<number>[] =>
            rollOverOptions.map(
                (option: LoggingSettingsOption): SelectOption<number> => ({
                    label: option.Description,
                    value: option.Value
                })
            ),
        [rollOverOptions]
    )

    const columns: DataTableColumn<LoggingSettingsRow, keyof LoggingSettingsRow>[] = useMemo(
        (): DataTableColumn<LoggingSettingsRow, keyof LoggingSettingsRow>[] => [
            {
                headerName: language.get(LoggingSettingsLanguage.LoggingSettings_Column_Component),
                field: 'Description',
                key: 'logging-settings-component',
                sortable: false,
                align: 'left',
                renderCell: (row: LoggingSettingsRow): string => row.Description
            },
            {
                headerName: language.get(LoggingSettingsLanguage.LoggingSettings_Column_Enabled),
                field: 'Description',
                key: 'logging-settings-enabled',
                sortable: false,
                disableRowSelection: true,
                align: 'center',
                renderCell: (row: LoggingSettingsRow): ReactNode => {
                    const checked: boolean = Boolean(draftSettings?.[row.component.enabledField])

                    return (
                        <LoggingSettingsCheckboxCell
                            testId={`logging-settings-enabled-${row.component.id}`}
                            field={row.component.enabledField}
                            checked={checked}
                            disabled={!canEdit || IsVarEmpty(draftSettings)}
                            updateDraftSetting={updateDraftSetting}
                        />
                    )
                }
            },
            {
                headerName: language.get(LoggingSettingsLanguage.LoggingSettings_Column_BackupLogs),
                field: 'Description',
                key: 'logging-settings-backup',
                sortable: false,
                disableRowSelection: true,
                align: 'center',
                renderCell: (row: LoggingSettingsRow): ReactNode => {
                    if (IsVarEmpty(row.component.backupField)) return <span data-testid={`logging-settings-backup-hidden-${row.component.id}`}>{EMPTY_STRING}</span>

                    const checked: boolean = Boolean(draftSettings?.[row.component.backupField])

                    return (
                        <LoggingSettingsCheckboxCell
                            testId={`logging-settings-backup-${row.component.id}`}
                            field={row.component.backupField}
                            checked={checked}
                            disabled={!canEdit || IsVarEmpty(draftSettings)}
                            updateDraftSetting={updateDraftSetting}
                        />
                    )
                }
            },
            {
                headerName: language.get(LoggingSettingsLanguage.LoggingSettings_Column_Level),
                field: 'Description',
                key: 'logging-settings-level',
                sortable: false,
                disableRowSelection: true,
                align: 'left',
                renderCell: (row: LoggingSettingsRow): ReactNode => {
                    const selectedValue: number = Number(draftSettings?.[row.component.levelField] ?? 0)

                    return (
                        <LoggingSettingsSelectCell
                            testId={`logging-settings-level-${row.component.id}`}
                            field={row.component.levelField}
                            value={selectedValue}
                            disabled={!canEdit || IsVarEmpty(draftSettings)}
                            options={levelSelectOptions}
                            containerClassName='logging-settings-list-cell-input logging-settings-list-cell-select logging-settings-list-level-select'
                            updateDraftSetting={updateDraftSetting}
                        />
                    )
                }
            },
            {
                headerName: language.get(LoggingSettingsLanguage.LoggingSettings_Column_RollOverFiles),
                field: 'Description',
                key: 'logging-settings-rollover',
                sortable: false,
                disableRowSelection: true,
                align: 'left',
                renderCell: (row: LoggingSettingsRow): ReactNode => {
                    const selectedValue: number = Number(draftSettings?.[row.component.rollOverFilesField] ?? 0)

                    return (
                        <LoggingSettingsSelectCell
                            testId={`logging-settings-rollover-${row.component.id}`}
                            field={row.component.rollOverFilesField}
                            value={selectedValue}
                            disabled={!canEdit || IsVarEmpty(draftSettings)}
                            options={rollOverSelectOptions}
                            containerClassName='logging-settings-list-cell-input logging-settings-list-cell-select'
                            updateDraftSetting={updateDraftSetting}
                        />
                    )
                }
            },
            {
                headerName: language.get(LoggingSettingsLanguage.LoggingSettings_Column_MaxFileSizeAsMB),
                field: 'Description',
                key: 'logging-settings-max-size',
                sortable: false,
                disableRowSelection: true,
                align: 'left',
                renderCell: (row: LoggingSettingsRow): ReactNode => {
                    const currentValue: number = Number(draftSettings?.[row.component.maxFileSizeField] ?? 0)

                    return (
                        <LoggingSettingsNumberCell
                            testId={`logging-settings-max-size-${row.component.id}`}
                            field={row.component.maxFileSizeField}
                            value={currentValue}
                            disabled={!canEdit || IsVarEmpty(draftSettings)}
                            updateDraftSetting={updateDraftSetting}
                        />
                    )
                }
            }
        ],
        [canEdit, draftSettings, language, levelSelectOptions, rollOverSelectOptions, updateDraftSetting]
    )

    const warningMessage: string = readOnlyWarning || language.get(LoggingSettingsLanguage.LoggingSettings_ReadOnlyWarning)

    return (
        <div className='logging-settings-list admin-list-container'>
            <div className='logging-settings-list-title-row'>
                <PageTitle title={RouterLang.PageTitle_loggingSettings} />
                <IconButton
                    customClassName='logging-settings-refresh-button'
                    iconName={IconName.REFRESH}
                    type={IconButtonType.BUTTON}
                    title={language.get(LoggingSettingsLanguage.LoggingSettings_Action_Refresh)}
                    onClick={handleRefresh}
                    isEnabled={!isLoading}
                />
            </div>
            {!canEdit ? (
                <div className='cWarningMsg' data-testid='logging-settings-warning'>
                    {warningMessage}
                </div>
            ) : null}
            <div className='logging-settings-list-table-container admin-table-list-container'>
                <DataTable<LoggingSettingsRow, keyof LoggingSettingsRow>
                    permissionName={AdminBackendPermissionNames.LOGGING}
                    localeText={{
                        noRows: language.get(LoggingSettingsLanguage.LoggingSettings_Empty)
                    }}
                    columns={columns}
                    data={[...rows]}
                    isLoading={isLoading}
                    toolbarDisabled
                    paginationDisabled
                    toolbarFooterSlot={
                        <>
                            {isDirty ? (
                                <>
                                    <Button
                                        color='primary'
                                        variant='contained'
                                        data-testid='logging-settings-save'
                                        disabled={!canEdit || isLoading}
                                        onClick={handleSave}
                                    >
                                        {language.get(LoggingSettingsLanguage.LoggingSettings_Action_Save)}
                                    </Button>
                                    <Button
                                        color='default'
                                        variant='contained'
                                        data-testid='logging-settings-cancel'
                                        disabled={isLoading}
                                        onClick={handleCancel}
                                    >
                                        {language.get(LoggingSettingsLanguage.LoggingSettings_Action_Cancel)}
                                    </Button>
                                </>
                            ) : null}
                        </>
                    }
                />
            </div>
        </div>
    )
}
